// ─────────────────────────────────────────────────────────────────────────────
// FeeManagement.js
// Self-contained Fee Management module for ALS Portal.
// Matches the existing navy/white/gray design system.
//
// STRUCTURE (all in one file for simplicity):
//   1. Design tokens
//   2. Helper functions
//   3. Shared UI primitives (icons, badges, modals shell)
//   4. FeeModal          — assign / edit a fee for one student or a whole class
//   5. StudentFeeModal   — view & inline-edit a student's fee breakdown
//   6. FeeManagementTab  — main student table with filters
//   7. FeeManagementView — top-level page with tabs (wires everything together)
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ChallanGenerationView from "./ChallanGenerationView";
import ChallanPrintingView from "./ChallanPrintingView";
import ChallanVerificationView from "./ChallanVerificationView";
import ReportTab from "./ReportTab";
import {
  fetchFeeAssignments,
  assignFeeToStudent, assignFeeToClass, assignFeeToStudents,
  updateComponent, removeComponent,
  updateConcession, updateDueDate, updateFeeStatus,
  deleteFeeEntry, deleteFeeEntries,
  consolidateSameMonthUnpaidFees,
} from "./feeApi";
import {
  FEE_COMPONENTS, FEE_CATEGORIES, CONCESSION_TYPES,
  ACADEMIC_YEARS, TABS,
} from "./feeConstants";
import FeeSetupView from "./FeeSetupView";
import {
  C, ledgerMono, fmt, fmtClass, fmtSection,
  Icon, Btn, Badge, Avatar, Card, CardHeader, TH, TD, Modal, Field, inputStyle, selectStyle,
} from "./feeUI";
import { alertDialog, confirmDialog } from "../../../utils/dialogs";
import { API_BASE_URL, getAuthHeaders } from "../../../utils/api";
import {
  sectionsForClassSession,
  uniqueClassNames,
} from "../../../utils/campusClasses";

// ─────────────────────────────────────────────────────────────────────────────
// 1. DOMAIN HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const normalizeStudent = (s) => ({
  ...s,
  class: s.class || s.class_name || "",
  section: s.section || s.class_section || "",
  rollNo: s.rollNo || s.student_id || s.id,
});

const ordinal = (d) => {
  const n = parseInt(d);
  if (n > 3 && n < 21) return `${n}th`;
  return `${n}${{ 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th"}`;
};

const calcConcession = (subtotal, conc) => {
  if (!conc) return 0;
  if (conc.type === "percentage") return (subtotal * conc.value) / 100;
  if (conc.type === "fixed")      return Math.min(conc.value, subtotal);
  if (conc.type === "waiver")     return subtotal;
  return 0;
};

// ── Pagination ─────────────────────────────────────────────────────────────
const Pagination = ({ page, total, perPage, onChange }) => {
  const pages = Math.ceil(total / perPage);
  if (pages <= 1) return null;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 22px", borderTop: `1px solid ${C.border}`, background: C.bg }}>
      <span style={{ fontSize: "12.5px", color: C.slate, fontWeight: 500 }}>
        Showing <strong style={{ color: C.text }}>{(page - 1) * perPage + 1}–{Math.min(page * perPage, total)}</strong> of <strong style={{ color: C.text }}>{total}</strong>
      </span>
      <div style={{ display: "flex", gap: "6px" }}>
        <Btn size="sm" variant="ghost" disabled={page === 1} onClick={() => onChange(page - 1)}>← Prev</Btn>
        {Array.from({ length: pages }, (_, i) => i + 1)
          .filter(p => p === 1 || p === pages || Math.abs(p - page) <= 1)
          .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i - 1] > 1) acc.push("…"); acc.push(p); return acc; }, [])
          .map((p, i) => p === "…"
            ? <span key={i} style={{ padding: "6px 4px", fontSize: "12px", color: C.slateLight }}>…</span>
            : <button
                key={i}
                onClick={() => onChange(p)}
                style={{
                  padding: "6px 11px", fontSize: "12.5px", fontWeight: 700, borderRadius: "7px",
                  border: p === page ? "none" : `1px solid ${C.border}`,
                  background: p === page ? C.navy : C.white,
                  color: p === page ? "#fff" : C.textMid,
                  cursor: "pointer",
                  boxShadow: p === page ? "0 2px 6px rgba(15,29,54,0.25)" : "none",
                  transition: "all .12s ease",
                }}
              >
                {p}
              </button>
          )}
        <Btn size="sm" variant="ghost" disabled={page === pages} onClick={() => onChange(page + 1)}>Next →</Btn>
      </div>
    </div>
  );
};

// ── Toast ──────────────────────────────────────────────────────────────────
const Toast = ({ toast }) => {
  if (!toast) return null;
  const bg = toast.type === "error" ? C.red : C.green;
  return (
    <>
      <style>{`@keyframes alsToastIn { from { opacity:0; transform: translateY(10px) scale(.97); } to { opacity:1; transform: translateY(0) scale(1); } }`}</style>
      <div style={{
        position: "fixed", bottom: "24px", right: "24px",
        padding: "13px 20px 13px 16px", borderRadius: "12px",
        background: C.white, color: C.text, fontSize: "13px", fontWeight: 600,
        boxShadow: "0 14px 34px rgba(15,29,54,0.20), 0 2px 8px rgba(15,29,54,0.10)",
        zIndex: 1100, display: "flex", alignItems: "center", gap: "10px",
        border: `1px solid ${C.border}`, borderLeft: `4px solid ${bg}`,
        animation: "alsToastIn .22s cubic-bezier(.2,.9,.3,1)",
        maxWidth: "360px",
      }}>
        <span style={{
          width: 22, height: 22, borderRadius: "50%", background: bg, color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "12px", flexShrink: 0,
        }}>
          {toast.type === "error" ? "✕" : "✓"}
        </span>
        {toast.text}
      </div>
    </>
  );
};

// ── Section divider ────────────────────────────────────────────────────────
const SectionTitle = ({ children }) => (
  <div style={{ fontSize: "12px", fontWeight: 700, color: C.slate, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "10px" }}>
    {children}
  </div>
);

// ── Summary box ────────────────────────────────────────────────────────────
const SummaryBox = ({ subtotal, concessionAmount, netPayable }) => (
  <div style={{ background: C.greenSoft, border: `1px solid ${C.greenBdr}`, borderRadius: "12px", padding: "16px 18px" }}>
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "6px" }}>
      <span style={{ color: C.textMid }}>Subtotal</span>
      <span style={{ fontWeight: 600 }}>{fmt(subtotal)}</span>
    </div>
    {concessionAmount > 0 && (
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "6px" }}>
        <span style={{ color: C.textMid }}>Discount</span>
        <span style={{ fontWeight: 600, color: C.amber }}>− {fmt(concessionAmount)}</span>
      </div>
    )}
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "15px", fontWeight: 800, borderTop: `1px solid ${C.greenBdr}`, paddingTop: "10px", marginTop: "6px" }}>
      <span>Net Payable</span>
      <span style={{ color: C.green }}>{fmt(netPayable)}</span>
    </div>
  </div>
);

// ── Ledger primitives ─────────────────────────────────────────────────────
const LedgerDivider = () => (
  <div style={{ borderTop: `1px dashed ${C.ledgerLine}`, margin: "24px 0" }} />
);

const LedgerSectionTitle = ({ children, right }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px", flexWrap: "wrap", gap: "10px" }}>
    <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
      <span style={{ width: 3, height: 15, background: C.amber, borderRadius: "2px", display: "inline-block" }} />
      <span style={{ fontSize: "12px", fontWeight: 700, color: C.slate, textTransform: "uppercase", letterSpacing: "0.7px" }}>{children}</span>
    </div>
    {right}
  </div>
);

const LedgerSealSummary = ({ subtotal, concessionAmount, netPayable }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "26px", flexWrap: "wrap" }}>
    <div style={{ flex: 1, minWidth: "200px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "8px 0", borderBottom: `1px dashed ${C.ledgerLine}` }}>
        <span style={{ color: C.textMid }}>Subtotal</span>
        <span style={{ ...ledgerMono, fontWeight: 600 }}>{fmt(subtotal)}</span>
      </div>
      {concessionAmount > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "8px 0", borderBottom: `1px dashed ${C.ledgerLine}` }}>
          <span style={{ color: C.amber }}>Discount</span>
          <span style={{ ...ledgerMono, fontWeight: 600, color: C.amber }}>− {fmt(concessionAmount)}</span>
        </div>
      )}
    </div>
    <div style={{
      minWidth: "160px", padding: "15px 24px", borderRadius: "16px",
      border: `3px double ${C.green}`, display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", flexShrink: 0,
      transform: "rotate(-3deg)", background: "rgba(46,125,50,0.05)",
      boxShadow: "0 4px 16px rgba(46,125,50,0.22)",
    }}>
      <span style={{ fontSize: "9px", fontWeight: 700, letterSpacing: "1.5px", color: C.green, textTransform: "uppercase" }}>Net Payable</span>
      <span style={{ ...ledgerMono, fontWeight: 700, fontSize: "20px", color: C.green, marginTop: "5px", whiteSpace: "nowrap" }}>{fmt(netPayable)}</span>
      <span style={{ fontSize: "8px", color: C.green, opacity: 0.75, marginTop: "4px", letterSpacing: "0.6px" }}>✓ VERIFIED</span>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// 4. FEE MODAL — assign / edit a fee
// ─────────────────────────────────────────────────────────────────────────────
const FeeModal = ({ isOpen, onClose, onSubmit, type, student, className, studentIds, session, feeAssignments, existingData }) => {
  const [category,     setCategory]     = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [customLabel,  setCustomLabel]  = useState("");
  const [components,   setComponents]   = useState([]);
  const [available,    setAvailable]    = useState([]);
  const [customComponentName, setCustomComponentName] = useState("");
  const [customComponentAmount, setCustomComponentAmount] = useState("");
  const [acYear,       setAcYear]       = useState("2025-2026");
  const [month,        setMonth]        = useState(new Date().toISOString().slice(0, 7));
  const [dueDate,      setDueDate]      = useState("10");
  const [useConc,      setUseConc]      = useState(false);
  const [concType,     setConcType]     = useState("percentage");
  const [concVal,      setConcVal]      = useState("");
  const [concReason,   setConcReason]   = useState("");
  const [saving,       setSaving]       = useState(false);

  // Default components for each category.
  // "monthly"  → only Tuition Fee (no Examination Fee)
  // "exam"     → Examination Fee + Tuition Fee
  // "sports"   → Sports Fee (+ optionally Tuition)
  // everything else → match the selected category component if available, else Tuition only
  const defaultComponentsForCategory = (catId) => {
    const byId = (id) => FEE_COMPONENTS.find(fc => fc.id === id);
    const make  = (fc) => ({ componentId: fc.id, componentLabel: fc.label, amount: fc.defaultValue });
    switch (catId) {
      case "monthly":
        return [byId("tuition")].filter(Boolean).map(make);
      case "exam":
        return [byId("examination"), byId("tuition")].filter(Boolean).map(make);
      case "sports":
        return [byId("sports")].filter(Boolean).map(make);
      case "library":
        return [byId("library")].filter(Boolean).map(make);
      case "transport":
        return [byId("transport")].filter(Boolean).map(make);
      case "hostel":
        return [byId("hostel")].filter(Boolean).map(make);
      default:
        return [byId("tuition")].filter(Boolean).map(make);
    }
  };

  const buildComponentsFromCategories = (catIds) => {
    const seen = new Set();
    return catIds.flatMap(defaultComponentsForCategory).filter((comp) => {
      if (seen.has(comp.componentLabel.toLowerCase())) return false;
      seen.add(comp.componentLabel.toLowerCase());
      return true;
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    if (existingData) {
      setCategory(existingData.categoryId);
      setSelectedCategories(existingData.categoryId ? [existingData.categoryId] : []);
      setCustomLabel(existingData.categoryId === "custom" ? existingData.categoryLabel : "");
      setComponents(existingData.components.map(c => ({ ...c })));
      setAvailable(FEE_COMPONENTS.filter(fc => !existingData.components.find(c => c.componentId === fc.id)));
      setAcYear(existingData.academicYear || "2025-2026");
      setMonth(existingData.month || new Date().toISOString().slice(0, 7));
      setDueDate(existingData.dueDate || "10");
      if (existingData.concession) {
        setUseConc(true); setConcType(existingData.concession.type);
        setConcVal(existingData.concession.value); setConcReason(existingData.concession.reason || "");
      } else { setUseConc(false); setConcType("percentage"); setConcVal(""); setConcReason(""); }
    } else {
      setCategory(""); setSelectedCategories([]); setCustomLabel("");
      setComponents([]);
      setAvailable(FEE_COMPONENTS);
      setAcYear("2025-2026"); setDueDate("10"); setUseConc(false); setConcType("percentage"); setConcVal(""); setConcReason("");
      const next = new Date(); next.setMonth(next.getMonth() + 1);
      const defaultMonth = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`;
      setMonth(defaultMonth);

      // Prefill recently added unpaid components for this student+month (if any)
      if ((type === "single" || type === "bulkMany") && feeAssignments && student?.id) {
        const studentFees = feeAssignments[String(student.id)] || {};
        const unpaid = Object.values(studentFees).find(
          (entry) => entry?.month === defaultMonth && entry?.status !== "Paid" && entry?.status !== "Waived"
        );
        if (unpaid) {
          setMonth(unpaid.month || defaultMonth);
          setDueDate(unpaid.dueDate || "10");
          setAcYear(unpaid.academicYear || "2025-2026");
          const cats = unpaid.selectedCategories || [unpaid.categoryId].filter(Boolean);
          setSelectedCategories(cats);
          setCategory(cats[0] || "");
          setComponents((unpaid.components || []).map((c) => ({ ...c })));
          setAvailable(FEE_COMPONENTS.filter((fc) => !(unpaid.components || []).find((c) => c.componentId === fc.id)));
        }
      }
    }
  }, [isOpen, existingData]);

  // When selected categories change (new form only), sync components to category defaults
  // while preserving amounts already entered for existing components.
  useEffect(() => {
    if (!isOpen || existingData || selectedCategories.length === 0) return;
    const defaults = buildComponentsFromCategories(selectedCategories);
    setComponents((prev) => {
      const byLabel = new Map(prev.map((c) => [c.componentLabel.toLowerCase(), c]));
      return defaults.map((d) => {
        const existing = byLabel.get(d.componentLabel.toLowerCase());
        return existing ? { ...d, amount: existing.amount } : d;
      });
    });
    setAvailable(FEE_COMPONENTS.filter(fc => !defaults.find(d => d.componentId === fc.id)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategories, isOpen, existingData]);

  const catInfo = useMemo(() => {
    if (selectedCategories.length === 0) return { id: "", label: "", prefix: "FEE" };
    if (selectedCategories.length === 1) {
      const selectedId = selectedCategories[0];
      if (selectedId === "custom" && customLabel) return { id: "custom", label: customLabel, prefix: "CST" };
      const single = FEE_CATEGORIES.find(c => c.id === selectedId) || { id: selectedId, label: "", prefix: "FEE" };
      return { id: single.id, label: single.label, prefix: single.prefix };
    }
    const labels = selectedCategories
      .map((id) => FEE_CATEGORIES.find(c => c.id === id)?.label || id)
      .filter(Boolean);
    return { id: "custom", label: labels.join(" + "), prefix: "MIX" };
  }, [selectedCategories, customLabel]);

  const subtotal         = useMemo(() => components.reduce((s, c) => s + (c.amount || 0), 0), [components]);
  const concessionAmount = useMemo(() => useConc && concVal ? calcConcession(subtotal, { type: concType, value: parseFloat(concVal) }) : 0, [useConc, concType, concVal, subtotal]);

  const existingMatchCount = useMemo(() => {
    if (type !== "bulkMany" || selectedCategories.length !== 1 || !month || !feeAssignments || !studentIds) return 0;
    const key = `${selectedCategories[0]}_${month}`;
    return studentIds.filter(id => feeAssignments[String(id)]?.[key]).length;
  }, [type, selectedCategories, month, feeAssignments, studentIds]);

  const addComponent = () => {
    if (!customComponentName.trim()) {
      alertDialog("Please enter a component name", { title: "Missing Component" });
      return;
    }

    if (components.some(c => c.componentLabel.toLowerCase() === customComponentName.trim().toLowerCase())) {
      alertDialog(`${customComponentName.trim()} is already added`, { title: "Duplicate Component" });
      return;
    }

    const amount = parseFloat(customComponentAmount) || 0;
    if (amount <= 0) {
      alertDialog("Please enter a valid amount greater than 0", { title: "Invalid Amount" });
      return;
    }

    setComponents(p => [...p, {
      componentId: `custom_${Date.now()}`,
      componentLabel: customComponentName.trim(),
      amount: amount
    }]);

    setCustomComponentName("");
    setCustomComponentAmount("");
  };

  const removeComp = (idx) => {
    setComponents(p => p.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (!selectedCategories.length) return alertDialog("Select at least one fee category", { title: "Missing Category" });
    if (selectedCategories.includes("custom") && selectedCategories.length === 1 && !customLabel.trim()) return alertDialog("Enter a custom category name", { title: "Missing Category Name" });
    if (!components.length) return alertDialog("Add at least one fee component", { title: "Missing Component" });
    const dd = parseInt(dueDate);
    if (!dueDate || isNaN(dd) || dd < 1 || dd > 31) return alertDialog("Enter a valid due date (1-31)", { title: "Invalid Due Date" });
    setSaving(true);
    const payload = {
      categoryId: catInfo.id,
      categoryLabel: catInfo.label,
      categoryPrefix: catInfo.prefix,
      selectedCategories: [...selectedCategories],
      components: components.map(c => ({
        componentId: c.componentId,
        componentLabel: c.componentLabel,
        amount: c.amount
      })),
      academicYear: acYear,
      month,
      dueDate,
      concession: useConc && concVal ? { type: concType, value: parseFloat(concVal), reason: concReason } : null,
    };
    if (session) payload.session = session;
    if (type === "single" && student) Object.assign(payload, { studentId: student.id, studentName: student.name, studentClass: student.class, studentRollNo: student.rollNo, studentSection: student.section });
    else if (type === "bulkMany" && studentIds) payload.studentIds = studentIds;
    else if (type === "bulk" && className) payload.className = className;
    await onSubmit(payload);
    setSaving(false);
    onClose();
  };

  const title    = type === "single" ? `Fee Setup — ${student?.name}`
    : type === "bulkMany" ? (className ? `Set Up Fee — ${fmtClass(className)}` : `Bulk Edit Fee — ${studentIds?.length || 0} Students`)
    : `Bulk Fee — Class ${className}`;
  const subtitle = type === "single" ? `${fmtClass(student?.class)} · Roll: ${student?.rollNo}`
    : type === "bulkMany" ? (className
        ? `${session ? `Session ${session} · ` : ""}${studentIds?.length || 0} student${studentIds?.length === 1 ? "" : "s"} in this class`
        : "Applies the same category, amounts & concession to every selected student")
    : `Applies to all students in this class${session ? ` · Session ${session}` : ""}`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} subtitle={subtitle} width="780px" accent bodyBg={C.parchment}>
      {/* Category */}
      <div style={{ display: "grid", gridTemplateColumns: selectedCategories.includes("custom") && selectedCategories.length === 1 && !existingData ? "1.5fr 1fr" : "1fr", gap: "16px" }}>
        <Field label="Fee Category *">
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "10px",
            padding: "12px",
            borderRadius: "12px",
            border: `1px solid ${C.ledgerLine}`,
            background: C.white,
          }}>
            {FEE_CATEGORIES.map((c) => {
              const checked = selectedCategories.includes(c.id);
              return (
                <label
                  key={c.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "10px 12px",
                    borderRadius: "10px",
                    border: `1px solid ${checked ? C.blue : C.border}`,
                    background: checked ? C.blueSoft : C.white,
                    cursor: existingData ? "not-allowed" : "pointer",
                    opacity: existingData ? 0.65 : 1,
                    fontSize: "13px",
                    fontWeight: 600,
                    color: checked ? C.blue : C.text,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={!!existingData}
                    onChange={() => {
                      const values = checked
                        ? selectedCategories.filter((id) => id !== c.id)
                        : [...selectedCategories, c.id];
                      setSelectedCategories(values);
                      setCategory(values[0] || "");
                    }}
                    style={{ width: 16, height: 16, accentColor: C.blue, cursor: existingData ? "not-allowed" : "pointer" }}
                  />
                  <span>{c.label}</span>
                </label>
              );
            })}
          </div>
        </Field>
        {selectedCategories.includes("custom") && selectedCategories.length === 1 && !existingData && (
          <Field label="Custom Name *">
            <input style={inputStyle} placeholder="e.g. Summer Camp Fee" value={customLabel} onChange={e => setCustomLabel(e.target.value)} />
          </Field>
        )}
      </div>

      {/* Meta */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px", marginTop: "18px" }}>
        <Field label="Academic Year">
          <select style={selectStyle} value={acYear} onChange={e => setAcYear(e.target.value)}>
            {ACADEMIC_YEARS.map(y => <option key={y}>{y}</option>)}
          </select>
        </Field>
        <Field label="Month">
          <input type="month" style={inputStyle} value={month} onChange={e => setMonth(e.target.value)} />
        </Field>
        <Field label="Due Date (day of month)">
          <input type="number" style={inputStyle} min="1" max="31" placeholder="e.g. 10" value={dueDate} onChange={e => setDueDate(e.target.value)} />
          {dueDate && !isNaN(dueDate) && parseInt(dueDate) >= 1 && parseInt(dueDate) <= 31 && (
            <span style={{ fontSize: "11px", color: C.slate, marginTop: "2px" }}>Due on {ordinal(dueDate)} of each month</span>
          )}
        </Field>
      </div>

      {/* Bulk-edit overwrite hint */}
      {type === "bulkMany" && selectedCategories.length > 0 && month && (
        <div style={{ display: "flex", alignItems: "center", gap: "9px", background: C.amberSoft, border: `1px solid rgba(196,154,42,0.35)`, borderRadius: "10px", padding: "11px 15px", marginTop: "18px", fontSize: "12.5px", color: C.amber, fontWeight: 600 }}>
          <span style={{ fontSize: "14px" }}>⚠</span>
          {selectedCategories.length === 1 && existingMatchCount > 0
            ? `${existingMatchCount} of ${studentIds.length} student${studentIds.length === 1 ? "" : "s"} already have a "${catInfo.label}" fee for ${month} — saving will overwrite their existing values.`
            : `This will create a new "${catInfo.label}" fee for ${month} on all ${studentIds.length} student${studentIds.length === 1 ? "" : "s"}.`}
        </div>
      )}

      <LedgerDivider />

      {/* Components */}
      <div>
        <LedgerSectionTitle
          right={
            <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
              <input
                type="text"
                style={{ ...inputStyle, minWidth: "180px" }}
                placeholder="Enter component name"
                value={customComponentName}
                onChange={e => setCustomComponentName(e.target.value)}
              />
              <input
                type="number"
                style={{ ...inputStyle, width: "120px", ...ledgerMono }}
                placeholder="Amount"
                value={customComponentAmount}
                onChange={e => setCustomComponentAmount(e.target.value)}
              />
              <Btn size="sm" onClick={addComponent}>
                <Icon.Plus /> Add Component
              </Btn>
            </div>
          }
        >
          Fee Components
        </LedgerSectionTitle>
        <div style={{ background: C.white, border: `1px solid ${C.ledgerLine}`, borderRadius: "12px", overflow: "hidden", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6), 0 2px 6px rgba(15,29,54,0.07)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "10px 14px", fontSize: "10.5px", fontWeight: 700, color: C.amber, background: C.amberSoft, borderBottom: `2px solid ${C.amber}`, textTransform: "uppercase", letterSpacing: "0.6px" }}>Component</th>
                <th style={{ textAlign: "right", padding: "10px 14px", fontSize: "10.5px", fontWeight: 700, color: C.amber, background: C.amberSoft, borderBottom: `2px solid ${C.amber}`, textTransform: "uppercase", letterSpacing: "0.6px" }}>Amount (PKR)</th>
                <th style={{ width: "36px", padding: "10px 14px", background: C.amberSoft, borderBottom: `2px solid ${C.amber}` }}></th>
              </tr>
            </thead>
            <tbody>
              {components.map((comp, idx) => {
                const req = FEE_COMPONENTS.find(fc => fc.id === comp.componentId)?.required;
                const isCustom = comp.componentId?.startsWith('custom_');
                return (
                  <tr key={idx} style={{ transition: "background .12s" }}>
                    <td style={{ padding: "12px 14px", fontSize: "13px", color: C.text, borderBottom: `1px solid ${C.ledgerLine}` }}>
                      {comp.componentLabel}
                      {req && <span title="Required" style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: C.red, marginLeft: 6, verticalAlign: "middle" }} />}
                      {isCustom && <span style={{ color: C.slateLight, fontSize: "11px", marginLeft: "6px" }}>(custom)</span>}
                    </td>
                    <td style={{ padding: "12px 14px", fontSize: "13px", textAlign: "right", borderBottom: `1px solid ${C.ledgerLine}` }}>
                      <input type="number" value={comp.amount}
                        onChange={e => setComponents(p => p.map((c, i) => i === idx ? { ...c, amount: parseFloat(e.target.value) || 0 } : c))}
                        style={{ ...inputStyle, ...ledgerMono, fontWeight: 600, width: "120px", textAlign: "right" }} />
                    </td>
                    <td style={{ padding: "12px 14px", borderBottom: `1px solid ${C.ledgerLine}` }}>
                      {!req && <button onClick={() => removeComp(idx)} style={{ background: "none", border: "none", cursor: "pointer", color: C.red, display: "flex" }}><Icon.Trash /></button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <LedgerDivider />

      {/* Discount */}
      <div>
        <LedgerSectionTitle
          right={
            <label style={{ display: "flex", alignItems: "center", gap: "7px", fontSize: "13px", color: C.textMid, cursor: "pointer" }}>
              <input type="checkbox" checked={useConc} onChange={e => setUseConc(e.target.checked)} /> Apply discount
            </label>
          }
        >
          Discount
        </LedgerSectionTitle>
        <div style={{ background: "rgba(138,108,31,0.05)", border: `1px dashed ${C.ledgerLine}`, borderRadius: "12px", padding: "15px 17px" }}>
          {useConc ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: "12px" }}>
              <Field label="Type">
                <select style={selectStyle} value={concType} onChange={e => setConcType(e.target.value)}>
                  {CONCESSION_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Value">
                <input type="number" style={{ ...inputStyle, ...ledgerMono }} placeholder="e.g. 10" value={concVal} onChange={e => setConcVal(e.target.value)} />
              </Field>
              <Field label="Reason (optional)">
                <input style={inputStyle} placeholder="e.g. Sibling discount" value={concReason} onChange={e => setConcReason(e.target.value)} />
              </Field>
            </div>
          ) : (
            <span style={{ fontSize: "12.5px", color: C.slate }}>No discount applied to this fee.</span>
          )}
        </div>
      </div>

      <LedgerDivider />

      {/* Summary */}
      <LedgerSealSummary subtotal={subtotal} concessionAmount={concessionAmount} netPayable={subtotal - concessionAmount} />

      {/* Actions */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "24px" }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={submit} disabled={saving}>
          {saving ? "Saving…" : type === "bulkMany" ? `Apply to ${studentIds?.length || 0} Students` : existingData ? "Update Fee" : "Assign Fee"}
        </Btn>
      </div>
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// 5. STUDENT FEE MODAL — view & inline-edit a student's fees
// ─────────────────────────────────────────────────────────────────────────────
const StudentFeeModal = ({ isOpen, onClose, student, feeCategories, onUpdateComp, onRemoveComp, onUpdateConc, onUpdateDue, onDelete }) => {
  const [activeKey,   setActiveKey]   = useState(null);
  const [editComp,    setEditComp]    = useState(null);
  const [editAmt,     setEditAmt]     = useState(0);
  const [editConc,    setEditConc]    = useState(false);
  const [concType,    setConcType]    = useState("percentage");
  const [concVal,     setConcVal]     = useState("");
  const [concReason,  setConcReason]  = useState("");
  const [editDue,     setEditDue]     = useState(false);
  const [newDue,      setNewDue]      = useState("");

  const keys    = Object.keys(feeCategories || {});
  const current = activeKey ? feeCategories[activeKey] : feeCategories?.[keys[0]];

  useEffect(() => {
    if (isOpen) { setActiveKey(keys[0] || null); setEditComp(null); setEditConc(false); setEditDue(false); }
  }, [isOpen, student]); // eslint-disable-line

  if (!isOpen || !student) return null;

  if (!current || !keys.length) return (
    <Modal isOpen={isOpen} onClose={onClose} title={student.name} subtitle="Fee Details" accent bodyBg={C.parchment}>
      <div style={{ textAlign: "center", padding: "48px 20px", color: C.slate }}>
        <div style={{ fontSize: "42px", marginBottom: "14px" }}>📄</div>
        <p style={{ fontSize: "14px" }}>No fees configured for this student.</p>
        <Btn variant="ghost" onClick={onClose} style={{ marginTop: "18px" }}>Close</Btn>
      </div>
    </Modal>
  );

  const switchKey = (k) => { setActiveKey(k); setEditComp(null); setEditConc(false); setEditDue(false); };

  const saveComp = async (compId) => { await onUpdateComp(student.id, current.categoryId, current.month, compId, parseFloat(editAmt) || 0); setEditComp(null); };
  const delComp  = async (compId, label) => { if (!(await confirmDialog(`Remove "${label}"?`, { title: "Remove Component", confirmLabel: "Remove", variant: "danger" }))) return; await onRemoveComp(student.id, current.categoryId, current.month, compId); };
  const saveConc = async () => { await onUpdateConc(student.id, current.categoryId, current.month, { type: concType, value: parseFloat(concVal), reason: concReason }); setEditConc(false); };
  const delConc  = async () => { if (!(await confirmDialog("Remove discount?", { title: "Remove Discount", confirmLabel: "Remove", variant: "danger" }))) return; await onUpdateConc(student.id, current.categoryId, current.month, null); setEditConc(false); };
  const saveDue  = async () => { await onUpdateDue(student.id, current.categoryId, current.month, newDue); setEditDue(false); };
  const delEntry = async () => { if (!(await confirmDialog(`Delete ${current.categoryLabel} (${current.month})?`, { title: "Delete Fee Entry", confirmLabel: "Delete", variant: "danger" }))) return; await onDelete(student.id, current.categoryId, current.month); keys.length <= 1 ? onClose() : switchKey(keys.find(k => k !== activeKey)); };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={student.name} subtitle={`${fmtClass(student.class)} · Roll: ${student.rollNo} · Section: ${fmtSection(student.section)}`} width="860px" accent bodyBg={C.parchment}>

      {keys.length > 1 && (
        <div style={{ display: "flex", gap: "4px", borderBottom: `1px solid ${C.ledgerLine}`, marginBottom: "20px", overflowX: "auto" }}>
          {keys.map(k => {
            const cat = feeCategories[k];
            const active = (activeKey || keys[0]) === k;
            return (
              <button key={k} onClick={() => switchKey(k)} style={{ padding: "10px 16px", fontSize: "13px", fontWeight: 700, border: "none", background: "transparent", cursor: "pointer", borderBottom: active ? `2px solid ${C.amber}` : "2px solid transparent", color: active ? C.navy : C.slate, whiteSpace: "nowrap", transition: "color .12s" }}>
                {cat.categoryLabel} <span style={{ fontSize: "11px", fontWeight: 400, color: active ? C.navy : C.slateLight }}>({cat.month})</span>
              </button>
            );
          })}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "12px", padding: "16px 18px", background: C.white, border: `1px solid ${C.ledgerLine}`, borderRadius: "12px", marginBottom: "20px", fontSize: "13px", boxShadow: "0 1px 3px rgba(15,29,54,0.05)" }}>
        <div><span style={{ color: C.slate, fontSize: "11px", textTransform: "uppercase", fontWeight: 700 }}>Year</span><br /><strong style={ledgerMono}>{current.academicYear || "—"}</strong></div>
        <div><span style={{ color: C.slate, fontSize: "11px", textTransform: "uppercase", fontWeight: 700 }}>Month</span><br /><strong style={ledgerMono}>{current.month || "—"}</strong></div>
        <div><span style={{ color: C.slate, fontSize: "11px", textTransform: "uppercase", fontWeight: 700 }}>Category</span><br /><Badge color="amber">{current.categoryLabel}</Badge></div>
        <div>
          <span style={{ color: C.slate, fontSize: "11px", textTransform: "uppercase", fontWeight: 700 }}>Due Date</span><br />
          {editDue ? (
            <span style={{ display: "flex", gap: "6px", alignItems: "center", marginTop: "4px" }}>
              <input type="number" min="1" max="31" style={{ ...inputStyle, ...ledgerMono, width: "60px" }} value={newDue} onChange={e => setNewDue(e.target.value)} />
              <button onClick={saveDue} style={{ background: "none", border: "none", color: C.blue, fontWeight: 700, cursor: "pointer", fontSize: "12px" }}>Save</button>
              <button onClick={() => setEditDue(false)} style={{ background: "none", border: "none", color: C.slate, cursor: "pointer", fontSize: "12px" }}>✕</button>
            </span>
          ) : (
            <span>
              <strong style={ledgerMono}>{current.dueDate ? ordinal(current.dueDate) : "—"}</strong>
              <button onClick={() => { setEditDue(true); setNewDue(current.dueDate || "10"); }} style={{ background: "none", border: "none", color: C.blue, cursor: "pointer", fontSize: "12px", marginLeft: "8px", fontWeight: 700 }}>Edit</button>
            </span>
          )}
        </div>
      </div>

      <LedgerDivider />

      <div>
        <LedgerSectionTitle>Fee Components</LedgerSectionTitle>
        <div style={{ background: C.white, border: `1px solid ${C.ledgerLine}`, borderRadius: "12px", overflow: "hidden", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6), 0 2px 6px rgba(15,29,54,0.07)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "10px 14px", fontSize: "10.5px", fontWeight: 700, color: C.amber, background: C.amberSoft, borderBottom: `2px solid ${C.amber}`, textTransform: "uppercase", letterSpacing: "0.6px" }}>Component</th>
                <th style={{ textAlign: "right", padding: "10px 14px", fontSize: "10.5px", fontWeight: 700, color: C.amber, background: C.amberSoft, borderBottom: `2px solid ${C.amber}`, textTransform: "uppercase", letterSpacing: "0.6px" }}>Amount (PKR)</th>
                <th style={{ textAlign: "left", padding: "10px 14px", fontSize: "10.5px", fontWeight: 700, color: C.amber, background: C.amberSoft, borderBottom: `2px solid ${C.amber}`, textTransform: "uppercase", letterSpacing: "0.6px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(current.components || []).map((comp, idx) => {
                const req     = FEE_COMPONENTS.find(fc => fc.id === comp.componentId)?.required;
                const editing = editComp === comp.componentId;
                return (
                  <tr key={comp.componentId || idx}>
                    <td style={{ padding: "12px 14px", fontSize: "13px", color: C.text, borderBottom: `1px solid ${C.ledgerLine}` }}>
                      {comp.componentLabel || comp.componentId}
                      {req && <span title="Required" style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: C.red, marginLeft: 6, verticalAlign: "middle" }} />}
                    </td>
                    <td style={{ padding: "12px 14px", fontSize: "13px", textAlign: "right", borderBottom: `1px solid ${C.ledgerLine}` }}>
                      {editing
                        ? <input type="number" value={editAmt} onChange={e => setEditAmt(e.target.value)} style={{ ...inputStyle, ...ledgerMono, fontWeight: 600, width: "110px", textAlign: "right" }} autoFocus />
                        : <strong style={ledgerMono}>{fmt(comp.amount)}</strong>}
                    </td>
                    <td style={{ padding: "12px 14px", borderBottom: `1px solid ${C.ledgerLine}` }}>
                      <div style={{ display: "flex", gap: "10px" }}>
                        {editing ? (
                          <>
                            <button onClick={() => saveComp(comp.componentId)} style={{ background: "none", border: "none", color: C.green, fontWeight: 700, cursor: "pointer", fontSize: "12px" }}>Save</button>
                            <button onClick={() => setEditComp(null)} style={{ background: "none", border: "none", color: C.slate, cursor: "pointer", fontSize: "12px" }}>Cancel</button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => { setEditComp(comp.componentId); setEditAmt(comp.amount); }} style={{ background: "none", border: "none", color: C.blue, fontWeight: 700, cursor: "pointer", fontSize: "12px" }}>Edit</button>
                            {!req && <button onClick={() => delComp(comp.componentId, comp.componentLabel)} style={{ background: "none", border: "none", color: C.red, fontWeight: 700, cursor: "pointer", fontSize: "12px" }}>Remove</button>}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <LedgerDivider />

      <div>
        <LedgerSectionTitle
          right={!current.concession && !editConc && (
            <button onClick={() => { setEditConc(true); setConcType("percentage"); setConcVal(""); setConcReason(""); }} style={{ background: "none", border: "none", color: C.blue, fontWeight: 700, cursor: "pointer", fontSize: "13px" }}>+ Add</button>
          )}
        >
          Discount
        </LedgerSectionTitle>
        <div style={{ background: "rgba(138,108,31,0.05)", border: `1px dashed ${C.ledgerLine}`, borderRadius: "12px", padding: "15px 17px" }}>
          {current.concession && !editConc && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: C.white, padding: "11px 15px", borderRadius: "10px", border: `1px solid ${C.ledgerLine}` }}>
              <span style={{ fontSize: "13px", fontWeight: 600 }}>
                {current.concession.type === "percentage" && `${current.concession.value}% Off`}
                {current.concession.type === "fixed"      && `Fixed: ${fmt(current.concession.value)}`}
                {current.concession.reason && <span style={{ fontWeight: 400, color: C.slate, marginLeft: "8px" }}>({current.concession.reason})</span>}
              </span>
              <div style={{ display: "flex", gap: "10px" }}>
                <button onClick={() => { setEditConc(true); setConcType(current.concession.type); setConcVal(current.concession.value); setConcReason(current.concession.reason || ""); }} style={{ background: "none", border: "none", color: C.blue, fontWeight: 700, cursor: "pointer", fontSize: "12px" }}>Edit</button>
                <button onClick={delConc} style={{ background: "none", border: "none", color: C.red, fontWeight: 700, cursor: "pointer", fontSize: "12px" }}>Remove</button>
              </div>
            </div>
          )}
          {editConc && (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: "10px", marginBottom: "10px" }}>
                <select style={selectStyle} value={concType} onChange={e => setConcType(e.target.value)}>
                  {CONCESSION_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
                <input type="number" style={{ ...inputStyle, ...ledgerMono }} placeholder="Value" value={concVal} onChange={e => setConcVal(e.target.value)} />
                <input style={inputStyle} placeholder="Reason (optional)" value={concReason} onChange={e => setConcReason(e.target.value)} />
              </div>
              <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                <Btn size="sm" variant="ghost" onClick={() => setEditConc(false)}>Cancel</Btn>
                <Btn size="sm" onClick={saveConc}>Save Discount</Btn>
              </div>
            </div>
          )}
          {!current.concession && !editConc && (
            <span style={{ fontSize: "12.5px", color: C.slate }}>No discount applied to this fee.</span>
          )}
        </div>
      </div>

      <LedgerDivider />

      <LedgerSealSummary subtotal={current.subtotal || 0} concessionAmount={current.concessionAmount || 0} netPayable={current.netPayable || 0} />

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "24px" }}>
        <Btn variant="danger" size="sm" onClick={delEntry}><Icon.Trash /> Delete Entry</Btn>
        <Btn variant="ghost" onClick={onClose}>Close</Btn>
      </div>
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// 5b. BULK DELETE MODAL
// ─────────────────────────────────────────────────────────────────────────────
const BulkDeleteModal = ({ isOpen, onClose, studentIds = [], feeAssignments, onConfirm }) => {
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [deleting,      setDeleting]    = useState(false);

  const entries = useMemo(() => {
    const map = new Map();
    studentIds.forEach(sid => {
      const cats = feeAssignments[String(sid)] || {};
      Object.values(cats).forEach(a => {
        const key = `${a.categoryId}_${a.month}`;
        if (!map.has(key)) map.set(key, { key, categoryId: a.categoryId, month: a.month, categoryLabel: a.categoryLabel, count: 0, total: 0 });
        const e = map.get(key);
        e.count += 1;
        e.total += a.netPayable || 0;
      });
    });
    return Array.from(map.values()).sort((a, b) => (b.month || "").localeCompare(a.month || "") || a.categoryLabel.localeCompare(b.categoryLabel));
  }, [studentIds, feeAssignments]);

  useEffect(() => { if (isOpen) setSelectedKeys(new Set()); }, [isOpen]);

  if (!isOpen) return null;

  const allChecked = entries.length > 0 && entries.every(e => selectedKeys.has(e.key));
  const toggleAll  = () => setSelectedKeys(allChecked ? new Set() : new Set(entries.map(e => e.key)));
  const toggleOne  = (key) => setSelectedKeys(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const selectedEntries = entries.filter(e => selectedKeys.has(e.key));
  const totalDeletions  = selectedEntries.reduce((s, e) => s + e.count, 0);

  const confirm = async () => {
    if (!selectedEntries.length) return;
    if (!(await confirmDialog(`Delete ${totalDeletions} fee record${totalDeletions === 1 ? "" : "s"} across ${studentIds.length} student${studentIds.length === 1 ? "" : "s"}? This cannot be undone.`, { title: "Bulk Delete Fees", confirmLabel: "Delete Fees", variant: "danger" }))) return;
    setDeleting(true);
    await onConfirm(selectedEntries.map(e => ({ categoryId: e.categoryId, month: e.month })));
    setDeleting(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Bulk Delete Fees" subtitle={`${studentIds.length} student${studentIds.length === 1 ? "" : "s"} selected`} width="640px">
      {entries.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: C.slate }}>
          <div style={{ fontSize: "42px", marginBottom: "14px" }}>📄</div>
          <p style={{ fontSize: "14px" }}>No fee entries found for the selected students.</p>
          <Btn variant="ghost" onClick={onClose} style={{ marginTop: "18px" }}>Close</Btn>
        </div>
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
            <SectionTitle>Fee Entries Found Among Selected Students</SectionTitle>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", fontWeight: 700, color: C.blue, cursor: "pointer" }}>
              <input type="checkbox" checked={allChecked} onChange={toggleAll} style={{ width: 15, height: 15, cursor: "pointer", accentColor: C.blue }} />
              Select all
            </label>
          </div>
          <Card>
            <div style={{ maxHeight: "320px", overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr><TH></TH><TH>Category</TH><TH>Month</TH><TH right>Students</TH><TH right>Total Payable</TH></tr>
                </thead>
                <tbody>
                  {entries.map(e => {
                    const checked = selectedKeys.has(e.key);
                    return (
                      <tr key={e.key} style={{ background: checked ? C.redSoft : "transparent", cursor: "pointer" }} onClick={() => toggleOne(e.key)}>
                        <TD><input type="checkbox" checked={checked} onChange={() => toggleOne(e.key)} onClick={ev => ev.stopPropagation()} style={{ width: 15, height: 15, cursor: "pointer", accentColor: C.red }} /></TD>
                        <TD><Badge color="amber">{e.categoryLabel}</Badge></TD>
                        <TD>{e.month}</TD>
                        <TD right><strong>{e.count}</strong></TD>
                        <TD right>{fmt(e.total)}</TD>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
      {entries.length > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "20px" }}>
          <span style={{ fontSize: "12px", color: selectedEntries.length ? C.red : C.slate, fontWeight: 600 }}>
            {selectedEntries.length > 0 ? `${totalDeletions} record${totalDeletions === 1 ? "" : "s"} will be permanently deleted` : "Select at least one fee entry to delete"}
          </span>
          <div style={{ display: "flex", gap: "10px" }}>
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn variant="danger" disabled={!selectedEntries.length || deleting} onClick={confirm}>
              <Icon.Trash /> {deleting ? "Deleting…" : `Delete Selected`}
            </Btn>
          </div>
        </div>
      )}
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// 5c. BULK ACTION BAR
// ─────────────────────────────────────────────────────────────────────────────
const BulkActionBar = ({ count, onEdit, onDelete, onClear }) => {
  if (!count) return null;
  return (
    <>
      <style>{`@keyframes alsBulkBarIn { from { opacity:0; transform: translate(-50%, 14px); } to { opacity:1; transform: translate(-50%, 0); } }`}</style>
      <div style={{
        position: "fixed", left: "50%", bottom: "24px", transform: "translateX(-50%)",
        background: C.navy, color: "#fff", borderRadius: "14px",
        padding: "11px 16px 11px 22px", display: "flex", alignItems: "center", gap: "16px",
        boxShadow: "0 16px 40px rgba(15,29,54,0.42)", zIndex: 900,
        animation: "alsBulkBarIn .18s ease-out",
      }}>
        <span style={{ fontSize: "13px", fontWeight: 700, whiteSpace: "nowrap" }}>
          {count} student{count === 1 ? "" : "s"} selected
        </span>
        <div style={{ width: 1, height: 22, background: "rgba(255,255,255,0.18)" }} />
        <div style={{ display: "flex", gap: "8px" }}>
          <Btn size="sm" variant="amber" onClick={onEdit}><Icon.Edit /> Bulk Edit Fees</Btn>
          <Btn size="sm" variant="danger" onClick={onDelete}><Icon.Trash /> Bulk Delete</Btn>
          <Btn size="sm" variant="ghost" onClick={onClear} style={{ background: "rgba(255,255,255,0.14)", color: "#fff", border: "none" }}>Clear</Btn>
        </div>
      </div>
    </>
  );
};

// ── Select-all checkbox ─────────────────────────────────────────────────────
const SelectAllCheckbox = ({ checked, indeterminate, onChange, title }) => {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = indeterminate; }, [indeterminate]);
  return <input ref={ref} type="checkbox" checked={checked} onChange={onChange} title={title} style={{ width: 16, height: 16, cursor: "pointer", accentColor: C.navy }} />;
};

// ─────────────────────────────────────────────────────────────────────────────
// 6. FEE MANAGEMENT TAB — student list with filters
// ─────────────────────────────────────────────────────────────────────────────
const FeeManagementTab = ({
  students, feeAssignments, isLoading, onAdd, onBulk, onView, onEdit, onDelete, onNextMonth,
  selected, onToggleSelect, onToggleMany, onClearSelection, onBulkEditMany, onBulkDeleteMany,
  campusId,
}) => {
  const [ses,      setSes]      = useState("all");
  const [cls,      setCls]      = useState("all");
  const [sec,      setSec]      = useState("all");
  const [search,   setSearch]   = useState("");
  const [page,     setPage]     = useState(1);
  const [perPage,  setPerPage]  = useState(10);
  const [campusClasses, setCampusClasses] = useState([]);
  const [campusSessions, setCampusSessions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const scope = campusId ? `?campus_id=${campusId}` : "";

    Promise.all([
      fetch(`${API_BASE_URL}/api/sessions${scope}`, { headers: getAuthHeaders() })
        .then((res) => (res.ok ? res.json() : []))
        .catch(() => []),
      fetch(`${API_BASE_URL}/api/classes${scope}`, { headers: getAuthHeaders() })
        .then((res) => (res.ok ? res.json() : []))
        .catch(() => []),
    ]).then(([sessionsData, classesData]) => {
      if (cancelled) return;
      setCampusSessions(Array.isArray(sessionsData) ? sessionsData : []);
      setCampusClasses(Array.isArray(classesData) ? classesData : []);
    });

    return () => {
      cancelled = true;
    };
  }, [campusId]);

  const selectedSessionId = useMemo(() => {
    if (ses === "all") return "";
    const match = campusSessions.find((session) => session.name === ses);
    return match ? String(match.id) : "";
  }, [ses, campusSessions]);

  const sessions = useMemo(() => {
    if (campusSessions.length) {
      return ["all", ...campusSessions.map((session) => session.name).filter(Boolean)];
    }
    return [
      "all",
      ...[...new Set(students.map((s) => s.academic_session))].filter(Boolean).sort((a, b) =>
        String(b).localeCompare(String(a)),
      ),
    ];
  }, [campusSessions, students]);

  const classes = useMemo(() => {
    if (campusClasses.length) {
      const names = uniqueClassNames(campusClasses, { sessionId: selectedSessionId });
      return ["all", ...names];
    }

    const pool =
      ses === "all" ? students : students.filter((student) => student.academic_session === ses);
    return ["all", ...[...new Set(pool.map((s) => s.class))].filter(Boolean).sort()];
  }, [campusClasses, selectedSessionId, students, ses]);

  const sections = useMemo(() => {
    if (campusClasses.length) {
      const values = sectionsForClassSession(campusClasses, {
        sessionId: selectedSessionId,
        className: cls === "all" ? "" : cls,
        students,
        formatSection: fmtSection,
      });
      return ["all", ...values];
    }

    const pool = students.filter((student) => {
      const matchesSession = ses === "all" || student.academic_session === ses;
      const matchesClass = cls === "all" || student.class === cls;
      return matchesSession && matchesClass;
    });
    return ["all", ...[...new Set(pool.map((s) => s.section))].filter(Boolean).sort()];
  }, [campusClasses, selectedSessionId, cls, students, ses]);

  useEffect(() => {
    setCls("all");
    setSec("all");
  }, [ses]);

  useEffect(() => {
    setSec("all");
  }, [cls]);

  useEffect(() => {
    if (cls !== "all" && !classes.includes(cls)) setCls("all");
  }, [classes, cls]);

  useEffect(() => {
    if (sec !== "all" && !sections.includes(sec)) setSec("all");
  }, [sections, sec]);

  const filtered = useMemo(() => students.filter(s => {
    const q = search.toLowerCase();
    return (cls === "all" || s.class === cls)
      && (sec === "all" || s.section === sec)
      && (ses === "all" || s.academic_session === ses)
      && (!q || s.name?.toLowerCase().includes(q) || String(s.rollNo || "").includes(q));
  }), [students, cls, sec, ses, search]);

  useEffect(() => setPage(1), [cls, sec, ses, search, perPage]);

  const rows    = filtered.slice((page - 1) * perPage, page * perPage);
  const rowIds  = useMemo(() => rows.map(s => s.id), [rows]);
  const filteredIds = useMemo(() => filtered.map(s => s.id), [filtered]);

  const allRowsSelected   = rowIds.length > 0 && rowIds.every(id => selected.has(id));
  const someRowsSelected  = rowIds.some(id => selected.has(id));
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every(id => selected.has(id));
  const selectedInFiltered  = filteredIds.filter(id => selected.has(id)).length;

  return (
    <Card>
      <CardHeader
        title="Fee Management"
        right={<Badge color="slate">{filtered.length} students</Badge>}
      />

      {/* Filters */}
      <div style={{
        padding: "16px 22px", display: "flex", gap: "14px", flexWrap: "wrap", alignItems: "flex-end",
        borderBottom: `1px solid ${C.border}`, background: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",
      }}>
        <Field label="Academic Session">
          <select style={{ ...selectStyle, minWidth: "150px" }} value={ses} onChange={e => setSes(e.target.value)}>
            {sessions.map(s => <option key={s} value={s}>{s === "all" ? "All Sessions" : s}</option>)}
          </select>
        </Field>
        <Field label="Class">
          <select style={{ ...selectStyle, minWidth: "130px" }} value={cls} onChange={e => setCls(e.target.value)}>
            {classes.map(c => <option key={c} value={c}>{c === "all" ? "All Classes" : fmtClass(c)}</option>)}
          </select>
        </Field>
        <Field label="Section">
          <select style={{ ...selectStyle, minWidth: "120px" }} value={sec} onChange={e => setSec(e.target.value)}>
            {sections.map(s => <option key={s} value={s}>{s === "all" ? "All Sections" : fmtSection(s)}</option>)}
          </select>
        </Field>
        <Field label="Per page">
          <select style={{ ...selectStyle, minWidth: "100px" }} value={perPage} onChange={e => setPerPage(Number(e.target.value))}>
            {[5, 10, 25, 50].map(n => <option key={n} value={n}>{n} rows</option>)}
          </select>
        </Field>
        <Field label="Search">
          <input style={{ ...inputStyle, minWidth: "220px" }} placeholder="Name or roll number…" value={search} onChange={e => setSearch(e.target.value)} />
        </Field>

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px" }}>
          {selectedInFiltered > 0 && (
            <span style={{ fontSize: "12px", color: C.blue, fontWeight: 700 }}>{selectedInFiltered} selected</span>
          )}
          {filtered.length > 0 && (
            <button
              onClick={() => onToggleMany(filteredIds, !allFilteredSelected)}
              style={{
                background: C.white, border: `1px solid ${C.border}`, borderRadius: "8px", padding: "8px 13px",
                fontSize: "12px", fontWeight: 700, color: C.blue, cursor: "pointer",
                boxShadow: "0 1px 2px rgba(15,29,54,0.05)", transition: "background .12s",
              }}
            >
              {allFilteredSelected ? "Deselect All" : `Select All (${filtered.length})`}
            </button>
          )}
          {cls !== "all" && filtered.length > 0 && (
            <Btn onClick={() => onBulk(cls)}><Icon.Plus /> Bulk Assign — {fmtClass(cls)}</Btn>
          )}
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: C.bg }}>
              <TH>
                <SelectAllCheckbox
                  checked={allRowsSelected}
                  indeterminate={someRowsSelected && !allRowsSelected}
                  onChange={() => onToggleMany(rowIds, !allRowsSelected)}
                  title="Select all on this page"
                />
              </TH>
              <TH>#</TH>
              <TH>Student</TH>
              <TH>Class</TH>
              <TH>Section</TH>
              <TH>Roll No.</TH>
              <TH>Fee Categories</TH>
              <TH right>Total Payable</TH>
              <TH>Actions</TH>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={9} style={{ padding: "70px", textAlign: "center", color: C.slateLight, fontSize: "13px" }}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: "70px", textAlign: "center", color: C.slate }}>
                <div style={{ fontSize: "34px", marginBottom: "10px" }}>🔍</div>
                <div style={{ fontSize: "13.5px", fontWeight: 500 }}>No students found</div>
              </td></tr>
            ) : rows.map((student, idx) => {
              const cats      = feeAssignments[String(student.id)] || {};
              const hasFees   = Object.keys(cats).length > 0;
              const total     = Object.values(cats).reduce((s, c) => s + (c.netPayable || 0), 0);
              const isChecked = selected.has(student.id);
              const rowBg     = isChecked ? C.blueSoft : (idx % 2 === 0 ? C.white : "#fafbfc");
              return (
                <tr key={student.id} style={{ background: rowBg, transition: "background .12s" }} onMouseEnter={e => { if (!isChecked) e.currentTarget.style.background = C.blueSoft; }} onMouseLeave={e => { e.currentTarget.style.background = rowBg; }}>
                  <TD><input type="checkbox" checked={isChecked} onChange={() => onToggleSelect(student.id)} style={{ width: 16, height: 16, cursor: "pointer", accentColor: C.navy }} /></TD>
                  <TD><span style={{ color: C.slateLight, fontWeight: 600 }}>{(page - 1) * perPage + idx + 1}</span></TD>
                  <TD>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <Avatar name={student.name} />
                      <span style={{ fontWeight: 700 }}>{student.name}</span>
                    </div>
                  </TD>
                  <TD><Badge color="blue">{fmtClass(student.class)}</Badge></TD>
                  <TD><span style={{ fontWeight: 600 }}>{fmtSection(student.section)}</span></TD>
                  <TD mono>{student.rollNo}</TD>
                  <TD>
                    {hasFees
                      ? <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                          {Object.values(cats).flatMap((cat, i) => {
                            const isPaid = cat.status === "Paid" || cat.status === "Waived";
                            const labels = Array.isArray(cat.selectedCategories) && cat.selectedCategories.length > 0
                              ? cat.selectedCategories.map((id) => ({
                                  id,
                                  label: FEE_CATEGORIES.find((item) => item.id === id)?.label || id,
                                }))
                              : [{ id: cat.categoryId, label: cat.categoryLabel }];
                            return [
                              ...labels.map((item, j) => (
                                <span key={`${i}-${j}`} style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                  <Badge color="amber">{item.label}</Badge>
                                  {!isPaid && (
                                    <button onClick={() => onDelete(student.id, cat.categoryId, cat.month, item.id)} style={{ background: "none", border: "none", cursor: "pointer", color: C.amber, fontWeight: 700, fontSize: "13px", lineHeight: 1, padding: 0 }}>×</button>
                                  )}
                                </span>
                              )),
                              isPaid ? <Badge key={`${i}-paid`} color="green">Paid</Badge> : null,
                            ];
                          })}
                        </div>
                      : <span style={{ color: C.slateLight, fontSize: "12px", fontStyle: "italic" }}>No fees assigned</span>
                    }
                  </TD>
                  <TD right><span style={{ fontWeight: 700, color: hasFees ? C.green : C.slateLight }}>{hasFees ? fmt(total) : "—"}</span></TD>
                  <TD>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                      {(() => {
                        const feeList = Object.values(cats);
                        const unpaidFees = feeList.filter((f) => f.status !== "Paid" && f.status !== "Waived");
                        const paidFees = feeList.filter((f) => f.status === "Paid" || f.status === "Waived");
                        return (
                          <>
                            {/* Add always available */}
                            <Btn size="sm" onClick={() => onAdd(student)} title="Add fee"><Icon.Plus /></Btn>
                            {/* Edit only when there is an unpaid fee */}
                            {unpaidFees.length > 0 && (
                              <Btn
                                size="sm"
                                variant="amber"
                                onClick={() => onEdit(student, unpaidFees[0].categoryId, unpaidFees[0])}
                                title="Edit fee"
                              >
                                <Icon.Edit />
                              </Btn>
                            )}
                            {/* Next Month when student has a paid fee */}
                            {paidFees.length > 0 && onNextMonth && (
                              <Btn
                                size="sm"
                                variant="success"
                                onClick={() => onNextMonth(student, paidFees[paidFees.length - 1])}
                                title="Print next month challan"
                                style={{ fontSize: "11px", fontWeight: 700, padding: "6px 10px" }}
                              >
                                Next Month
                              </Btn>
                            )}
                            {hasFees && (
                              <Btn size="sm" variant="blue" onClick={() => onView(student)} title="View details"><Icon.Eye /></Btn>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </TD>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination page={page} total={filtered.length} perPage={perPage} onChange={setPage} />

      <BulkActionBar
        count={selected.size}
        onEdit={() => onBulkEditMany(Array.from(selected))}
        onDelete={() => onBulkDeleteMany(Array.from(selected))}
        onClear={onClearSelection}
      />
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// 7. FEES HOME — 4-card launcher
// ─────────────────────────────────────────────────────────────────────────────
const SECTION_META = {
  setup:      { icon: Icon.Layers,   accent: C.amber, description: "Set up one fee for an entire class at once — applied to every student and visible in Fee Management & Challan Generation." },
  management: { icon: Icon.Cash,     accent: C.navy,  description: "Assign fee categories, components, and concessions to students or whole classes." },
  challan:    { icon: Icon.FileList, accent: C.blue,  description: "Review fee assignments due and select which students to generate challans for." },
  printing:   { icon: Icon.Print,    accent: C.green, description: "Preview and print challans for the students selected in Challan Generation." },
  verify:     { icon: Icon.Cash,     accent: C.blue,  description: "Review paid challan proofs uploaded by parents, verify them, and mark challans as Paid." },
  report:     { icon: Icon.BarChart, accent: C.amber, description: "View collection rates, pending dues, concessions, and class-wise analytics." },
};

const FeesHome = ({ stats, onSelect, tabs }) => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: "22px" }}>
    {tabs.map(tab => {
      const meta = SECTION_META[tab.id];
      const Icn  = meta.icon;
      return (
        <button
          key={tab.id}
          onClick={() => onSelect(tab.id)}
          style={{
            textAlign: "left", cursor: "pointer", background: C.white,
            border: `1px solid ${C.border}`, borderRadius: "16px", padding: "24px",
            boxShadow: "0 2px 8px rgba(15,29,54,0.06)", transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
            display: "flex", flexDirection: "column", position: "relative", overflow: "hidden",
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = "translateY(-4px)";
            e.currentTarget.style.boxShadow = "0 16px 36px rgba(15,29,54,0.14)";
            e.currentTarget.style.borderColor = "transparent";
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = "none";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(15,29,54,0.06)";
            e.currentTarget.style.borderColor = C.border;
          }}
        >
          {/* top accent strip */}
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "4px", background: meta.accent }} />

          <div style={{
            width: 50, height: 50, borderRadius: "14px",
            background: `linear-gradient(135deg, ${meta.accent} 0%, ${meta.accent}cc 100%)`,
            color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
            marginBottom: "18px", boxShadow: `0 6px 16px ${meta.accent}40`,
          }}>
            <Icn size={22} />
          </div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "17px", fontWeight: 700, color: C.text, marginBottom: "8px" }}>{tab.label}</div>
          <div style={{ fontSize: "12.5px", color: C.textMid, lineHeight: 1.6, marginBottom: "18px", flex: 1 }}>{meta.description}</div>
          <div style={{ fontSize: "12px", fontWeight: 700, color: meta.accent, display: "flex", alignItems: "center", gap: "6px", paddingTop: "14px", borderTop: `1px solid ${C.border}` }}>
            {stats[tab.id]} <Icon.ArrowRight />
          </div>
        </button>
      );
    })}
  </div>
);

const SectionHeader = ({ onBack }) => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        marginBottom: "20px",
      }}
    >
      <button
        type="button"
        onClick={onBack}
        style={{
          background: "none",
          border: "none",
          color: C.blue,
          cursor: "pointer",
          fontSize: "13px",
          fontWeight: 700,
          padding: 0,
        }}
      >
        ← Back to Fee Management
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// 8. FEE MANAGEMENT VIEW — top-level page
// ─────────────────────────────────────────────────────────────────────────────
const CLOSED_MODAL       = { isOpen: false, type: null, student: null, className: null, studentIds: null, session: null, existingData: null };
const CLOSED_DETAILS     = { isOpen: false, student: null, feeCategories: {} };
const CLOSED_BULK_DELETE = { isOpen: false, studentIds: [] };

// All roles with fee access now get full fee management (no restricted view).
const CONTROLLER_ALLOWED_VIEWS = ["home", "report"];

const FeeManagementView = ({ students: enrolledStudents = [], campusId, onExit }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const loginRole    = localStorage.getItem("als_role");
  // The new "admin" role (formerly controller) has full campus access including fees.
  const isController  = false;

  // No roles are currently restricted — full fee management is available.
  const visibleTabs = useMemo(
    () => (isController ? TABS.filter(t => t.id === "report") : TABS),
    [isController],
  );

  const getInitialView = () => {
    const requested = location.state?.activeTab || "home";
    if (isController && !CONTROLLER_ALLOWED_VIEWS.includes(requested)) return "home";
    return requested;
  };

  const [activeView,   setActiveView]   = useState(getInitialView);
  const [feeData,      setFeeData]      = useState({});
  const [isLoading,    setIsLoading]    = useState(true);
  const [toast,        setToast]        = useState(null);
  const [modal,        setModal]        = useState(CLOSED_MODAL);
  const [details,      setDetails]      = useState(CLOSED_DETAILS);
  const [selectedChallanIds, setSelectedChallanIds] = useState(new Set());
  const [selectedFeeIds, setSelectedFeeIds] = useState(new Set());
  const [bulkDeleteModal, setBulkDeleteModal] = useState(CLOSED_BULK_DELETE);

  const students = useMemo(() => enrolledStudents.map(normalizeStudent), [enrolledStudents]);

  useEffect(() => {
    const requested = location.state?.activeTab || "home";
    // Block controllers from landing on a restricted section via router state.
    if (isController && !CONTROLLER_ALLOWED_VIEWS.includes(requested)) {
      setActiveView("home");
      return;
    }
    setActiveView(requested);
  }, [location.state?.activeTab, isController]);

  const openView = useCallback((view) => {
    navigate(location.pathname, {
      state: { ...(location.state || {}), activeTab: view },
    });
  }, [location.pathname, location.state, navigate]);

  const returnHome = useCallback(() => {
    navigate(location.pathname, {
      replace: true,
      state: { ...(location.state || {}), activeTab: "home" },
    });
  }, [location.pathname, location.state, navigate]);

  // Safety net: if activeView is ever set to a restricted section for a
  // controller (e.g. stale state), bounce back to home immediately.
  useEffect(() => {
    if (isController && !CONTROLLER_ALLOWED_VIEWS.includes(activeView)) {
      setActiveView("home");
    }
  }, [isController, activeView]);

  const notify = useCallback((text, type = "success") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const reload = useCallback(async () => {
    setFeeData(await consolidateSameMonthUnpaidFees());
  }, []);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        setFeeData(await consolidateSameMonthUnpaidFees());
      } catch (e) { console.error(e); notify("Failed to load data", "error"); }
      finally { setIsLoading(false); }
    })();
  }, [notify]);

  // Keep Paid/Unpaid in sync when status changes from Student Fee Details or other views
  useEffect(() => {
    const onStatus = () => { reload(); };
    window.addEventListener("fee-status-updated", onStatus);
    window.addEventListener("focus", onStatus);
    return () => {
      window.removeEventListener("fee-status-updated", onStatus);
      window.removeEventListener("focus", onStatus);
    };
  }, [reload]);

  const reloadAndSync = useCallback(async (studentId) => {
    const updated = await consolidateSameMonthUnpaidFees(studentId);
    setFeeData(updated);
    if (studentId) setDetails(prev => prev.isOpen && prev.student?.id === studentId
      ? { ...prev, feeCategories: updated[String(studentId)] || {} }
      : prev
    );
  }, []);

  const handleSubmit = async (payload) => {
    try {
      if (payload.studentId) {
        const res = await assignFeeToStudent(payload);
        notify(`${payload.categoryLabel} assigned — ${fmt(res.netPayable)}`);
      } else if (payload.studentIds) {
        const res = await assignFeeToStudents(payload, payload.studentIds, students);
        notify(`${payload.categoryLabel} applied to ${res.length} student${res.length === 1 ? "" : "s"}`);
        setSelectedFeeIds(new Set());
      } else {
        const res = await assignFeeToClass(payload, students);
        notify(`${payload.categoryLabel} assigned to ${res.length} students`);
      }
      await reload();
    } catch (e) { console.error(e); notify("Failed to assign fee", "error"); }
  };

  const handleDelete = async (studentId, categoryId, month, categoryToRemove = null) => {
    const all = await fetchFeeAssignments();
    const sid = String(studentId);
    const key = `${categoryId}_${month}`;
    const entry = all[sid]?.[key];

    if (entry && categoryToRemove && Array.isArray(entry.selectedCategories) && entry.selectedCategories.length > 1) {
      const keptCategories = entry.selectedCategories.filter((id) => id !== categoryToRemove);
      const keptLabels = keptCategories
        .map((id) => FEE_CATEGORIES.find((c) => c.id === id)?.label || id)
        .filter(Boolean);
      const componentMap = {
        monthly: ["tuition"],
        exam: ["examination", "tuition"],
        sports: ["sports"],
        library: ["library"],
        transport: ["transport"],
        hostel: ["hostel"],
      };
      const wantedComponentIds = [...new Set(keptCategories.flatMap((id) => componentMap[id] || []))];
      const nextComponents = (entry.components || []).filter((comp) => wantedComponentIds.includes(comp.componentId));
      const nextSubtotal = nextComponents.reduce((sum, comp) => sum + (comp.amount || 0), 0);
      const nextConcessionAmount = calcConcession(nextSubtotal, entry.concession);

      all[sid][key] = {
        ...entry,
        selectedCategories: keptCategories,
        categoryLabel: keptLabels.join(" + "),
        components: nextComponents,
        subtotal: nextSubtotal,
        concessionAmount: nextConcessionAmount,
        netPayable: nextSubtotal - nextConcessionAmount,
        lastUpdated: new Date().toISOString(),
      };
      localStorage.setItem("fee_assignments_v2", JSON.stringify(all));
      notify("Fee category removed");
      await reloadAndSync(studentId);
      return;
    }

    await deleteFeeEntry(studentId, categoryId, month);
    notify("Fee entry deleted");
    await reloadAndSync(studentId);
    if (details.student?.id === studentId) setDetails(CLOSED_DETAILS);
  };

  const handleBulkDelete = async (entries) => {
    const studentIds = bulkDeleteModal.studentIds;
    const deletions   = studentIds.flatMap(sid => entries.map(en => ({ studentId: sid, ...en })));
    try {
      await deleteFeeEntries(deletions);
      notify(`Deleted ${deletions.length} fee record${deletions.length === 1 ? "" : "s"}`);
      setSelectedFeeIds(new Set());
      await reload();
    } catch (e) { console.error(e); notify("Failed to delete fees", "error"); }
  };

  const handleUpdateComp = async (sid, cid, month, compId, amount) => {
    await updateComponent(sid, cid, month, compId, amount);
    notify("Component updated");
    await reloadAndSync(sid);
  };

  const handleRemoveComp = async (sid, cid, month, compId) => {
    await removeComponent(sid, cid, month, compId);
    notify("Component removed");
    await reloadAndSync(sid);
  };

  const handleUpdateConc = async (sid, cid, month, conc) => {
    await updateConcession(sid, cid, month, conc);
    notify(conc ? "Discount applied" : "Discount removed");
    await reloadAndSync(sid);
  };

  const handleUpdateDue = async (sid, cid, month, due) => {
    await updateDueDate(sid, cid, month, due);
    notify("Due date updated");
    await reloadAndSync(sid);
  };

  const handleUpdateStatus = async (sid, cid, month, status) => {
    await updateFeeStatus(sid, cid, month, status);
    notify(status === "Paid" ? "Marked as Paid" : "Marked as Unpaid");
    await reloadAndSync(sid);
  };

  const studentsWithFees = Object.keys(feeData).length;
  const totalAssignments = Object.values(feeData).reduce((sum, cats) => sum + Object.keys(cats).length, 0);
  const distinctClasses  = new Set(students.map(s => s.class).filter(Boolean)).size;
  const homeStats = {
    setup:      `${distinctClasses} class${distinctClasses === 1 ? "" : "es"} available to set up`,
    management: `${studentsWithFees} of ${students.length} students have fees assigned`,
    challan:    `${totalAssignments} fee assignment${totalAssignments === 1 ? "" : "s"} to review`,
    printing:   "Browse and print all generated challans",
    verify:     "Verify parent-uploaded paid challans",
    report:     "Collection, pending dues & analytics",
  };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "24px", paddingBottom: selectedFeeIds.size > 0 ? "90px" : "24px", transition: "padding-bottom .18s ease" }}>

      {activeView === "home" ? (
        <>
          {onExit && (
            <button
              type="button"
              onClick={onExit}
              style={{
                background: "none",
                border: "none",
                color: C.blue,
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 700,
                marginBottom: "20px",
                padding: 0,
              }}
            >
              ← Back to Campus Selection
            </button>
          )}
          <FeesHome stats={homeStats} onSelect={openView} tabs={visibleTabs} />
        </>
      ) : (
        <>
          <SectionHeader sectionId={activeView} onBack={returnHome} />

          {activeView === "setup" && !isController && (
            <FeeSetupView
              students={students}
              feeAssignments={feeData}
              campusId={campusId}
              onConfigure={(cls, session, studentIds) => setModal({ isOpen: true, type: "bulkMany", student: null, className: cls, studentIds, session, existingData: null })}
            />
          )}

          {activeView === "management" && !isController && (
            <FeeManagementTab
              students={students}
              feeAssignments={feeData}
              isLoading={isLoading}
              campusId={campusId}
              onAdd={(student) => setModal({ isOpen: true, type: "single", student, className: null, studentIds: null, existingData: null })}
              onBulk={(cls) => setModal({ isOpen: true, type: "bulk", student: null, className: cls, studentIds: null, existingData: null })}
              onView={(student) => setDetails({ isOpen: true, student, feeCategories: feeData[String(student.id)] || {} })}
              onEdit={(student, _, catData) => setModal({ isOpen: true, type: "single", student, className: null, studentIds: null, existingData: catData })}
              onNextMonth={(student, paidFee) => {
                navigate(`/student-fee-details/${student.id}`, {
                  state: {
                    openNextMonth: true,
                    paidFeeKey: paidFee ? `${paidFee.categoryId}_${paidFee.month}` : null,
                    fromFees: true,
                  },
                });
              }}
              onDelete={handleDelete}
              selected={selectedFeeIds}
              onToggleSelect={(id) => setSelectedFeeIds(prev => {
                const next = new Set(prev);
                next.has(id) ? next.delete(id) : next.add(id);
                return next;
              })}
              onToggleMany={(ids, checked) => setSelectedFeeIds(prev => {
                const next = new Set(prev);
                ids.forEach(id => checked ? next.add(id) : next.delete(id));
                return next;
              })}
              onClearSelection={() => setSelectedFeeIds(new Set())}
              onBulkEditMany={(ids) => setModal({ isOpen: true, type: "bulkMany", student: null, className: null, studentIds: ids, existingData: null })}
              onBulkDeleteMany={(ids) => setBulkDeleteModal({ isOpen: true, studentIds: ids })}
            />
          )}

          {activeView === "challan" && !isController && (
            <ChallanGenerationView
              students={students}
              feeAssignments={feeData}
              campusId={campusId}
              selected={selectedChallanIds}
              onSelectedChange={setSelectedChallanIds}
              onUpdateDue={handleUpdateDue}
              onFeesChanged={reloadAndSync}
              onOpenBulkPage={(params) => navigate("/bulk-challan-generation", {
                state: { ...params, returnPath: location.pathname },
              })}
            />
          )}

          {activeView === "printing" && !isController && (
            <ChallanPrintingView
              students={students}
              feeAssignments={feeData}
              campusId={campusId}
              selected={selectedChallanIds}
              onSelectedChange={setSelectedChallanIds}
              onGoToGeneration={() => openView("challan")}
            />
          )}

          {activeView === "verify" && !isController && (
            <ChallanVerificationView
              campusId={campusId}
              onFeesChanged={reloadAndSync}
            />
          )}

          {activeView === "report" && (
            <ReportTab
              students={students}
              feeAssignments={feeData}
            />
          )}
        </>
      )}

      {/* Modals — never mounted for controllers, since none of the actions
          that open them are reachable in the restricted view set. */}
      {!isController && (
        <>
          <FeeModal
            isOpen={modal.isOpen}
            onClose={() => setModal(CLOSED_MODAL)}
            onSubmit={handleSubmit}
            type={modal.type}
            student={modal.student}
            className={modal.className}
            studentIds={modal.studentIds}
            session={modal.session}
            feeAssignments={feeData}
            existingData={modal.existingData}
          />

          <BulkDeleteModal
            isOpen={bulkDeleteModal.isOpen}
            onClose={() => setBulkDeleteModal(CLOSED_BULK_DELETE)}
            studentIds={bulkDeleteModal.studentIds}
            feeAssignments={feeData}
            onConfirm={handleBulkDelete}
          />

          <StudentFeeModal
            isOpen={details.isOpen}
            onClose={() => setDetails(CLOSED_DETAILS)}
            student={details.student}
            feeCategories={details.feeCategories}
            onUpdateComp={handleUpdateComp}
            onRemoveComp={handleRemoveComp}
            onUpdateConc={handleUpdateConc}
            onUpdateDue={handleUpdateDue}
            onDelete={handleDelete}
          />
        </>
      )}

      <Toast toast={toast} />
    </div>
  );
};

export default FeeManagementView;